import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { MarkdownGuidanceDetails } from '../../components/ui/MarkdownGuidanceDetails';
import { MarkdownGuidanceField } from '../../components/ui/MarkdownGuidanceField';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import {
  COMPETENCY_AUDIENCE_OPTIONS,
  Competency,
  CompetencyQuestion,
  CompetencyQuestionUpsertPayload,
  EVALUATION_QUESTION_TYPE_OPTIONS,
  EvaluationQuestionType,
  PerformanceIndicator,
  createCompetencyQuestion,
  deleteCompetencyQuestion,
  listCompetencies,
  listCompetencyQuestions,
  listPerformanceIndicators,
  updateCompetencyQuestion,
} from '../../services/peoplePerformanceApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type QuestionFormState = Omit<CompetencyQuestionUpsertPayload, 'sortOrder'>;

const emptyForm: QuestionFormState = {
  competencyId: '',
  prompt: '',
  guidanceMarkdown: '',
  questionType: 'Scale',
  isActive: true,
};

function questionTypeLabel(value?: string | null): string {
  return EVALUATION_QUESTION_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

function audienceLabel(value?: string | null): string {
  return COMPETENCY_AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

function buildPayload(item: CompetencyQuestion, sortOrder: number): CompetencyQuestionUpsertPayload {
  return {
    competencyId: item.competencyId,
    prompt: item.prompt,
    guidanceMarkdown: item.guidanceMarkdown,
    questionType: item.questionType,
    sortOrder,
    isActive: item.isActive,
  };
}

export function CompetencyQuestionsPage() {
  const [searchParams] = useSearchParams();
  const initialCompetencyId = searchParams.get('competencyId') ?? '';
  const initialIndicatorId = searchParams.get('indicatorId') ?? '';
  const { confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.competencies');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [items, setItems] = useState<CompetencyQuestion[]>([]);
  const [indicators, setIndicators] = useState<PerformanceIndicator[]>([]);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [formIndicatorId, setFormIndicatorId] = useState(initialIndicatorId);
  const [form, setForm] = useState<QuestionFormState>({
    ...emptyForm,
    competencyId: initialCompetencyId,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [indicatorFilter, setIndicatorFilter] = useState(initialIndicatorId);
  const [competencyFilter, setCompetencyFilter] = useState(initialCompetencyId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const formCompetencies = useMemo(
    () => competencies.filter((item) => item.indicatorId === formIndicatorId),
    [competencies, formIndicatorId],
  );

  const filterCompetencies = useMemo(
    () =>
      indicatorFilter
        ? competencies.filter((item) => item.indicatorId === indicatorFilter)
        : competencies,
    [competencies, indicatorFilter],
  );

  const selectedFormCompetency = useMemo(
    () => formCompetencies.find((item) => item.id === form.competencyId) ?? null,
    [form.competencyId, formCompetencies],
  );

  async function loadCatalog(): Promise<void> {
    try {
      const [indicatorsResult, competenciesResult] = await Promise.all([
        listPerformanceIndicators({ pageSize: 100, activeOnly: true }),
        listCompetencies({ pageSize: 100, activeOnly: true }),
      ]);
      setIndicators(indicatorsResult.items);
      setCompetencies(competenciesResult.items);

      if (initialCompetencyId && !initialIndicatorId) {
        const matched = competenciesResult.items.find((item) => item.id === initialCompetencyId);
        if (matched) {
          setFormIndicatorId(matched.indicatorId);
          setIndicatorFilter(matched.indicatorId);
        }
      }
    } catch {
      toast.error('Falha ao carregar indicadores e competências.');
    }
  }

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const result = await listCompetencyQuestions({
        pageSize: 100,
        competencyId: competencyFilter || undefined,
      });
      const filtered = indicatorFilter
        ? result.items.filter((item) => item.indicatorId === indicatorFilter)
        : result.items;
      setItems(filtered);
    } catch {
      toast.error('Falha ao carregar perguntas.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateCompetencyQuestion(item.id, buildPayload(item, sortOrder));
    },
    onReordered: () => loadData(),
  });

  useEffect(() => {
    void loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competencyFilter, indicatorFilter]);

  function resetForm(): void {
    setEditingId(null);
    const nextIndicatorId = indicatorFilter || '';
    const nextCompetencyId =
      competencyFilter &&
      (!nextIndicatorId ||
        competencies.some((item) => item.id === competencyFilter && item.indicatorId === nextIndicatorId))
        ? competencyFilter
        : '';
    setFormIndicatorId(nextIndicatorId);
    setForm({ ...emptyForm, competencyId: nextCompetencyId });
  }

  function startEdit(item: CompetencyQuestion): void {
    setEditingId(item.id);
    setFormIndicatorId(item.indicatorId);
    setForm({
      competencyId: item.competencyId,
      prompt: item.prompt,
      guidanceMarkdown: item.guidanceMarkdown ?? '',
      questionType: (item.questionType as EvaluationQuestionType) || 'Scale',
      isActive: item.isActive,
    });
    expandForm();
  }

  function handleFormIndicatorChange(indicatorId: string): void {
    setFormIndicatorId(indicatorId);
    setForm((current) => ({ ...current, competencyId: '' }));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;
    if (!form.competencyId) {
      toast.error('Selecione o indicador e a competência.');
      return;
    }
    setSaving(true);
    try {
      const payload: CompetencyQuestionUpsertPayload = {
        ...form,
        sortOrder: editingId
          ? items.find((item) => item.id === editingId)?.sortOrder ?? 0
          : nextSortOrder(items),
      };
      if (editingId) {
        await updateCompetencyQuestion(editingId, payload);
        toast.success('Pergunta atualizada.');
        collapseForm(resetForm);
      } else {
        await createCompetencyQuestion(payload);
        toast.success('Pergunta criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao salvar pergunta.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirmDelete({
        title: 'Excluir pergunta',
        message: 'Excluir esta pergunta?',
      }))
    ) {
      return;
    }
    try {
      await deleteCompetencyQuestion(id);
      toast.success('Pergunta excluída.');
      await loadData();
    } catch {
      toast.error('Falha ao excluir pergunta.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Perguntas de avaliação"
        description="Selecione o indicador e a competência (já classificada por nível). A pergunta herda o nível automaticamente."
        actions={(
          <div className="flex flex-wrap gap-2">
            <Link to="/people/competencies" className="erp-btn-secondary text-sm">
              Competências
            </Link>
            <Link to="/people/performance-indicators" className="erp-btn-secondary text-sm">
              Indicadores
            </Link>
          </div>
        )}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar pergunta' : 'Nova pergunta'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          isEditing={!!editingId}
          createSummary="Clique para expandir e cadastrar uma nova pergunta"
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Indicador
                <select
                  className="erp-input"
                  value={formIndicatorId}
                  required
                  onChange={(event) => handleFormIndicatorChange(event.target.value)}
                >
                  <option value="">Selecione...</option>
                  {indicators.map((indicator) => (
                    <option key={indicator.id} value={indicator.id}>
                      {indicator.name} ({audienceLabel(indicator.audience)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Competência
                <select
                  className="erp-input"
                  value={form.competencyId}
                  required
                  disabled={!formIndicatorId}
                  onChange={(event) => setForm({ ...form, competencyId: event.target.value })}
                >
                  <option value="">
                    {formIndicatorId ? 'Selecione...' : 'Selecione o indicador primeiro'}
                  </option>
                  {formCompetencies.map((competency) => (
                    <option key={competency.id} value={competency.id}>
                      {competency.name}
                    </option>
                  ))}
                </select>
              </label>
              {selectedFormCompetency ? (
                <p className="md:col-span-2 text-sm text-secondary-600">
                  Nível da pergunta:{' '}
                  <span className="font-medium text-secondary-900">
                    {audienceLabel(selectedFormCompetency.audience)}
                  </span>
                  {' · '}
                  Indicador: {selectedFormCompetency.indicatorName}
                </p>
              ) : null}
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.questionType}
                  required
                  onChange={(event) =>
                    setForm({ ...form, questionType: event.target.value as EvaluationQuestionType })
                  }
                >
                  {EVALUATION_QUESTION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label md:col-span-2">
                Pergunta
                <textarea
                  className="erp-input"
                  rows={3}
                  value={form.prompt}
                  required
                  onChange={(event) => setForm({ ...form, prompt: event.target.value })}
                />
              </label>
              <MarkdownGuidanceField
                value={form.guidanceMarkdown ?? ''}
                onChange={(guidanceMarkdown) => setForm({ ...form, guidanceMarkdown })}
              />
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                Ativa
              </label>
            </div>
            <FormActionBar onCancel={() => collapseForm(resetForm)} className="erp-btn-actions mt-4">
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-3">
        <label className="erp-label min-w-[240px]">
          Filtrar por indicador
          <select
            className="erp-input mt-1"
            value={indicatorFilter}
            onChange={(event) => {
              setIndicatorFilter(event.target.value);
              setCompetencyFilter('');
            }}
          >
            <option value="">Todos</option>
            {indicators.map((indicator) => (
              <option key={indicator.id} value={indicator.id}>
                {indicator.name} ({audienceLabel(indicator.audience)})
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label min-w-[240px]">
          Filtrar por competência
          <select
            className="erp-input mt-1"
            value={competencyFilter}
            onChange={(event) => setCompetencyFilter(event.target.value)}
          >
            <option value="">Todas</option>
            {filterCompetencies.map((competency) => (
              <option key={competency.id} value={competency.id}>
                {competency.name} ({audienceLabel(competency.audience)})
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Ordem</th>
                <th>Indicador</th>
                <th>Nível</th>
                <th>Competência</th>
                <th>Pergunta</th>
                <th>Tipo</th>
                <th>Ativa</th>
                {canWrite ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 8 : 7} className="text-secondary-500">
                    Nenhuma pergunta cadastrada.
                  </td>
                </tr>
              ) : (
                sortedItems.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      <SortOrderControls
                        canMoveUp={index > 0}
                        canMoveDown={index < sortedItems.length - 1}
                        disabled={moving}
                        onMoveUp={() => void move(index, 'up')}
                        onMoveDown={() => void move(index, 'down')}
                      />
                    </td>
                    <td>{item.indicatorName}</td>
                    <td>{audienceLabel(item.audience)}</td>
                    <td>
                      {item.competencyName}
                    </td>
                    <td>
                      <div>{item.prompt}</div>
                      <MarkdownGuidanceDetails value={item.guidanceMarkdown} />
                    </td>
                    <td>{questionTypeLabel(item.questionType)}</td>
                    <td>
                      <ActiveStatusBadge
                        active={item.isActive}
                        activeLabel="Sim"
                        inactiveLabel="Não"
                      />
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="flex gap-2">
                          <EditIconButton onClick={() => startEdit(item)} />
                          <DeleteIconButton onClick={() => void handleDelete(item.id)} />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
